import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface WorkflowBlueprint {
  id: string;
  user_id: string;
  status: 'draft' | 'complete';
  industry: string | null;
  data_source_type: string | null;
  schema_snapshot: any[];
  cleaning_rules: {
    remove_columns?: string[];
    normalize?: Record<string, boolean>;
    exclude_from_analysis?: string[];
  };
  prediction_goals: string[];
  dashboard_config: {
    type?: string;
    metrics?: string[];
    visual_format?: string;
  };
  ai_warnings: any[];
  last_schema_hash: string | null;
  created_at: string;
  updated_at: string;
}

export const useWorkflowBlueprint = () => {
  const queryClient = useQueryClient();

  const { data: blueprint, isLoading } = useQuery({
    queryKey: ['workflow-blueprint'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from('workflow_blueprints')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching blueprint:', error);
        return null;
      }
      return data as WorkflowBlueprint | null;
    },
  });

  const { mutateAsync: saveBlueprint, isPending: isSaving } = useMutation({
    mutationFn: async (updates: Partial<WorkflowBlueprint>) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      if (blueprint?.id) {
        const { data, error } = await supabase
          .from('workflow_blueprints')
          .update(updates)
          .eq('id', blueprint.id)
          .select()
          .single();
        if (error) throw error;
        return data;
      } else {
        const { data, error } = await supabase
          .from('workflow_blueprints')
          .insert({ ...updates, user_id: user.id })
          .select()
          .single();
        if (error) throw error;
        return data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow-blueprint'] });
    },
    onError: (err) => {
      toast.error(`Failed to save: ${err.message}`);
    },
  });

  const isSetupComplete = blueprint?.status === 'complete';

  const computeCompletionStep = (): number => {
    if (!blueprint) return 0;
    if (!blueprint.industry || !blueprint.data_source_type) return 0;
    if (!blueprint.cleaning_rules || Object.keys(blueprint.cleaning_rules).length === 0) return 1;
    if (!blueprint.prediction_goals || (blueprint.prediction_goals as string[]).length === 0) return 2;
    if (!blueprint.dashboard_config || Object.keys(blueprint.dashboard_config).length === 0) return 3;
    return 4;
  };

  return {
    blueprint,
    isLoading,
    saveBlueprint,
    isSaving,
    isSetupComplete,
    currentStep: computeCompletionStep(),
  };
};
